import { useState, useRef, useEffect } from "react";
import { Languages } from "lucide-react";
import { ChevronDown } from "lucide-react";
import i18n from "i18next";
import { languageManager } from "../services/languageManager";

const languages = [
  { code: "en", label: "English", flag: "🇺🇸" },
  { code: "sv", label: "Svenska", flag: "🇸🇪" },
];

const LanguageDropdown = () => {
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const currentLanguage = languages.find((lang) => lang.code === i18n.language) || languages[0];

  const changeLang = async (lang: string) => {
    if (lang === i18n.language) {
      setOpen(false);
      return;
    }

    languageManager.startLanguageChange();

    try {
      await i18n.changeLanguage(lang);
      localStorage.setItem("lang", lang);
    } catch (error) {
      console.error("Language change failed", error);
      languageManager.requestFinished();
    } finally {
      setOpen(false);
    }
  };

  // 🔥 Close on outside click (same logic as your navbar)
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (!dropdownRef.current?.contains(e.target as Node)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1 px-3 py-2 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 transition"
      >
        <span className="font-medium">{currentLanguage.code.toUpperCase()}</span>
        <ChevronDown size={16} />
      </button>

      <div
        className={`absolute right-0 mt-2 w-40 bg-white rounded-lg shadow-xl border border-gray-100 z-50 transform transition-all duration-200 ${open
          ? "opacity-100 scale-100 translate-y-0"
          : "opacity-0 scale-95 -translate-y-2 pointer-events-none"
          }`}
      >
        {languages.map((lang) => (
          <button
            key={lang.code}
            onClick={() => changeLang(lang.code)}
            className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-primary transition-colors"
          >
            <span>{lang.flag}</span>
            {lang.label}
          </button>
        ))}
      </div>
    </div>
  );
};

export default LanguageDropdown;